<script lang="ts">
  import { onDestroy, onMount } from "svelte"
  import {
    customerImportStatus,
    refreshCustomerImportStatus,
  } from "../../../../../_shared/_logic/customer-import-status"
  import CustomerImportReviewStep from "../../../../../_shared/_modules/customer-import-review-step"
  import { customerImportStatusPresentation } from "../../_shared/_logic/status-presentation"

  let interval: ReturnType<typeof setInterval> | null = null

  const importStatus = $derived($customerImportStatus.status)
  const status = $derived(importStatus?.status ?? null)
  const draft = $derived(importStatus?.draft ?? null)
  const progress = $derived(status ? customerImportStatusPresentation(status) : null)
  const isTerminal = $derived(progress?.terminal ?? false)

  const formatDateTime = (value?: string | null) => {
    if (!value) return "–"

    const parsed = new Date(value)

    if (Number.isNaN(parsed.getTime())) return "–"

    return parsed.toLocaleString("de-DE", {
      dateStyle: "short",
      timeStyle: "short",
    })
  }

  onMount(() => {
    void refreshCustomerImportStatus()
    interval = setInterval(() => {
      void refreshCustomerImportStatus()
    }, 5000)
  })

  onDestroy(() => {
    if (interval) clearInterval(interval)
  })
</script>

<section class="import-status-overlay">
  <div class="intro">
    <h3>Kehrbezirksimport</h3>
    <p>{status?.message ?? importStatus?.message ?? "Importstatus wird geladen ..."}</p>
  </div>

  {#if status && progress}
    <div class="progress-card">
      <div class="progress-heading">
        {#if !progress.indeterminate && !progress.terminal}
          <strong>{progress.percent}% der Datensätze</strong>
        {/if}
        <span role="status">{progress.label}</span>
      </div>
      {#if !progress.terminal || progress.successful}
        <progress
          class="progress-track"
          aria-label={progress.label}
          max="100"
          value={progress.indeterminate ? undefined : progress.percent}
        ></progress>
      {/if}
      <dl>
        <div>
          <dt>Phase</dt>
          <dd>{status.phase}</dd>
        </div>
        <div>
          <dt>Status</dt>
          <dd>{status.status}</dd>
        </div>
        <div>
          <dt>Gestartet</dt>
          <dd>{formatDateTime(status.started_at)}</dd>
        </div>
        <div>
          <dt>Letzte Aktualisierung</dt>
          <dd>{formatDateTime(status.updated_at)}</dd>
        </div>
        <div>
          <dt>Timeout</dt>
          <dd>{formatDateTime(importStatus?.timeout_at)}</dd>
        </div>
      </dl>
    </div>
  {:else}
    <p class="muted">Aktueller Fortschritt wird geladen ...</p>
  {/if}

  {#if isTerminal && draft?.id}
    <CustomerImportReviewStep draftId={draft.id} fallbackToLatestCommitted={false} />
  {/if}
</section>

<style lang="less">
  .import-status-overlay {
    display: grid;
    gap: 1rem;
    color: var(--text-invers-100);
  }

  .intro {
    h3,
    p {
      margin: 0;
      color: var(--text-invers-100);
    }

    p {
      margin-top: 0.35rem;
      color: color-mix(in srgb, var(--text-invers-100) 72%, transparent);
    }
  }

  .progress-card {
    display: grid;
    gap: 0.8rem;
    border: 1px solid rgba(0, 0, 0, 0.14);
    padding: 1rem;
    background: #fff;
  }

  .progress-heading {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 1rem;
  }

  .progress-track {
    width: 100%;
    height: 8px;
    overflow: hidden;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.12);
  }

  dl {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 0.7rem;
    margin: 0;
  }

  dt {
    font-size: 0.75rem;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.58);
  }

  dd {
    margin: 0.2rem 0 0;
    font-weight: 700;
  }

  .muted {
    color: color-mix(in srgb, var(--text-invers-100) 68%, transparent);
  }
</style>
